import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Building, Users as UsersIcon, Banknote, Calendar, Download, ArrowUpDown,
  ArrowUp, ArrowDown, Layers, ParkingSquare, Search, X, Activity,
} from "lucide-react";
import { vmListMieteinheiten, vmGetKpis, vmExportCsvUrl } from "../lib/api";
import { fmtInt, fmtEur, fmtDec, fmtQm, fmtDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";

const STATUS_LABEL = {
  aktuell: "Aktuell",
  aktuell_und_zukuenftig: "Verlängert",
  zukuenftig: "Zukünftig",
  leerstand: "Leerstand",
  unbekannt: "—",
};

const STATUS_CHIP = {
  aktuell: "bg-navy/8 text-navy",
  aktuell_und_zukuenftig: "bg-gold/15 text-gold",
  zukuenftig: "bg-emerald-50 text-emerald-800",
  leerstand: "bg-rose-50 text-rose-800",
  unbekannt: "bg-navy/5 text-navy/55",
};

const KpiCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="border border-navy/12 bg-white px-5 py-5 flex flex-col gap-2"
  >
    <div className="flex items-center justify-between">
      <div className="pb-eyebrow">{label}</div>
      <Icon size={16} strokeWidth={1.5} className="text-gold" />
    </div>
    <div className="font-display text-[28px] sm:text-[32px] font-semibold text-navy leading-none tracking-tight">
      {value}
    </div>
    {sub && <div className="text-xs text-navy/55 font-sans">{sub}</div>}
  </div>
);

const ScopeToggle = ({ scope, onChange }) => (
  <div className="inline-flex border border-navy/15" role="radiogroup">
    <button
      data-testid="vm-scope-aktuell"
      type="button"
      onClick={() => onChange("aktuell")}
      className={`px-3 py-1.5 text-xs uppercase tracking-wider font-sans transition-colors ${
        scope === "aktuell"
          ? "bg-navy text-white"
          : "bg-white text-navy/70 hover:bg-navy/5"
      }`}
    >
      Nur aktuell
    </button>
    <button
      data-testid="vm-scope-zukuenftig"
      type="button"
      onClick={() => onChange("aktuell_zukuenftig")}
      className={`px-3 py-1.5 text-xs uppercase tracking-wider font-sans transition-colors border-l border-navy/15 ${
        scope === "aktuell_zukuenftig"
          ? "bg-navy text-white"
          : "bg-white text-navy/70 hover:bg-navy/5"
      }`}
    >
      Inkl. zukünftig
    </button>
  </div>
);

const COLUMNS = [
  { key: "gebaeude",            label: "Gebäude",       align: "left",  width: "w-[10%]" },
  { key: "mieter",              label: "Mieter",        align: "left",  width: "w-[18%]" },
  { key: "nutzung",             label: "Nutzung",       align: "left",  width: "w-[16%]" },
  { key: "status",              label: "Status",        align: "left",  width: "w-[10%]" },
  { key: "mietflaeche_m2",      label: "Fläche",        align: "right", width: "w-[8%]"  },
  { key: "stellplaetze",        label: "Stellpl.",      align: "right", width: "w-[6%]"  },
  { key: "jahresnettomiete_eur", label: "Jahresmiete",  align: "right", width: "w-[10%]" },
  { key: "eur_pro_m2",          label: "€/m²",          align: "right", width: "w-[6%]"  },
  { key: "mietende",            label: "Mietende",      align: "left",  width: "w-[8%]"  },
  { key: "indexierungsart",     label: "Indexierung",   align: "left",  width: "w-[8%]"  },
];

export default function Vermietung() {
  const [scope, setScope] = useState("aktuell");
  const [kpis, setKpis] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("jahresnettomiete_eur");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [gebaeudeFilter, setGebaeudeFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, m] = await Promise.all([vmGetKpis(scope), vmListMieteinheiten()]);
      setKpis(k);
      setRows(m.items || []);
    } catch (e) {
      toast.error("Vermietungsdaten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  // Filtering + sorting on client side
  const filteredRows = useMemo(() => {
    let out = rows;
    if (scope === "aktuell") {
      out = out.filter((r) => r.aktueller_mieter);
    } else {
      out = out.filter((r) => r.aktueller_mieter || r.zukuenftiger_mieter);
    }
    if (gebaeudeFilter) {
      out = out.filter((r) => (r.gebaeude || "") === gebaeudeFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) =>
        [r.mieter, r.nutzung, r.gebaeude]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q))
      );
    }
    const cmp = (a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }
      const as = String(av);
      const bs = String(bv);
      return sortDir === "asc" ? as.localeCompare(bs, "de") : bs.localeCompare(as, "de");
    };
    return [...out].sort(cmp);
  }, [rows, scope, gebaeudeFilter, search, sortBy, sortDir]);

  const gebaeudeOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.gebaeude).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  // For chart axis: max jahresmiete
  const maxBucketMiete = useMemo(() => {
    if (!kpis?.mietendeprofil?.length) return 1;
    return Math.max(...kpis.mietendeprofil.map((b) => b.jahresnettomiete_eur || 0), 1);
  }, [kpis]);

  const k = kpis?.kpis;

  return (
    <div className="space-y-8 py-2">
      <PageHeader
        eyebrow="Newton Campus · Vermietung"
        title="Rent Roll"
        subtitle={
          scope === "aktuell"
            ? "Aktuelle Mieter (Stand 2026) — Basis für KPIs und WAULT."
            : "Aktuelle und vermarktbare zukünftige Mieter (NEWMARK, Siemens Energy, Impetum X)."
        }
        testid="vermietung-header"
        actions={
          <>
            <ScopeToggle scope={scope} onChange={setScope} />
            <a
              data-testid="vm-export-csv"
              href={vmExportCsvUrl()}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider border border-navy/30 text-navy hover:bg-navy hover:text-white transition-colors font-sans"
            >
              <Download size={14} strokeWidth={1.6} />
              CSV
            </a>
          </>
        }
      />

      {/* KPI Cards */}
      <section
        data-testid="vermietung-kpis"
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
      >
        <KpiCard
          testid="kpi-jahresmiete"
          icon={Banknote}
          label="Jahresnettomiete"
          value={k ? fmtEur(k.jahresnettomiete_eur) : "—"}
          sub={k ? `${fmtEur(k.monatsnettomiete_eur)} / Monat` : null}
        />
        <KpiCard
          testid="kpi-flaeche"
          icon={Layers}
          label="Mietfläche"
          value={k ? fmtQm(k.mietflaeche_m2) : "—"}
          sub={k && k.eur_pro_m2_avg ? `Ø ${fmtDec(k.eur_pro_m2_avg)} €/m²` : null}
        />
        <KpiCard
          testid="kpi-wault"
          icon={Calendar}
          label="WAULT"
          value={k && k.wault_jahre != null ? `${fmtDec(k.wault_jahre)} J` : "—"}
          sub="Gewichtet · Jahresmiete"
        />
        <KpiCard
          testid="kpi-mieter"
          icon={UsersIcon}
          label="Mieter"
          value={k ? fmtInt(k.anzahl_mieter) : "—"}
          sub={k ? `${fmtInt(k.anzahl_einheiten)} Mieteinheiten` : null}
        />
        <KpiCard
          testid="kpi-stellplaetze"
          icon={ParkingSquare}
          label="Stellplätze"
          value={k ? fmtInt(k.stellplaetze) : "—"}
          sub="Innen + außen"
        />
      </section>

      {/* Mietendeprofil + Pro Mieter */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Mietendeprofil */}
        <div
          data-testid="mietendeprofil"
          className="border border-navy/12 bg-white p-5"
        >
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="pb-eyebrow">Mietendeprofil</div>
              <div className="font-display text-lg text-navy mt-1">
                Auslauf der Mietverhältnisse
              </div>
            </div>
            <div className="text-xs text-navy/55 font-sans">
              Stand · {kpis?.stand ? fmtDate(kpis.stand) : "—"}
            </div>
          </div>
          <ul className="space-y-3">
            {(kpis?.mietendeprofil || []).map((b) => {
              const pct = maxBucketMiete > 0
                ? Math.max(2, Math.round((b.jahresnettomiete_eur / maxBucketMiete) * 100))
                : 0;
              const hasData = (b.jahresnettomiete_eur || 0) > 0 || (b.einheiten || 0) > 0;
              return (
                <li key={b.key} className="grid grid-cols-[120px_1fr_auto] items-center gap-3">
                  <div className="text-xs text-navy/70 font-sans">{b.label}</div>
                  <div className="h-6 bg-navy/5 relative">
                    {hasData && (
                      <div
                        className="h-full bg-navy transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    )}
                  </div>
                  <div className="text-xs text-right font-sans">
                    <div className="text-navy font-medium">
                      {hasData ? fmtEur(b.jahresnettomiete_eur) : "—"}
                    </div>
                    <div className="text-navy/55">
                      {b.einheiten} Einheit{b.einheiten === 1 ? "" : "en"}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Pro Mieter */}
        <div
          data-testid="pro-mieter"
          className="border border-navy/12 bg-white p-5"
        >
          <div className="pb-eyebrow">Mieter</div>
          <div className="font-display text-lg text-navy mt-1 mb-4">
            Anteil an Jahresmiete
          </div>
          <ul className="space-y-3">
            {(kpis?.pro_mieter || []).filter((m) => m.jahresnettomiete_eur > 0).map((m) => (
              <li key={m.mieter} className="grid grid-cols-[1fr_auto] gap-3 items-center">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm text-navy font-medium truncate" title={m.mieter}>
                      {m.mieter}
                    </div>
                    {m.zukuenftig && !m.aktuell && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-800 uppercase tracking-wider">
                        Zukünftig
                      </span>
                    )}
                  </div>
                  <div className="h-1.5 bg-navy/5 mt-1.5">
                    <div
                      className="h-full bg-gold"
                      style={{ width: `${Math.max(2, m.anteil_miete_prozent || 0)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-navy font-medium font-sans">
                    {fmtEur(m.jahresnettomiete_eur)}
                  </div>
                  <div className="text-[11px] text-navy/55 font-sans">
                    {fmtDec(m.anteil_miete_prozent || 0)} %
                  </div>
                </div>
              </li>
            ))}
            {(!kpis?.pro_mieter || !kpis.pro_mieter.length) && (
              <li className="text-sm text-navy/55">Keine Daten</li>
            )}
          </ul>
        </div>
      </section>

      {/* Pro Gebäude */}
      <section className="border border-navy/12 bg-white" data-testid="pro-gebaeude">
        <div className="px-5 pt-5">
          <div className="pb-eyebrow">Gebäude-Aufteilung</div>
          <div className="font-display text-lg text-navy mt-1 mb-4">
            Bestand pro Gebäude
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-y border-navy/10 bg-navy/3 text-navy/65">
                <th className="text-left px-5 py-2 font-sans font-medium uppercase tracking-wider text-[10px]">Gebäude</th>
                <th className="text-right px-5 py-2 font-sans font-medium uppercase tracking-wider text-[10px]">Einheiten</th>
                <th className="text-right px-5 py-2 font-sans font-medium uppercase tracking-wider text-[10px]">Fläche</th>
                <th className="text-right px-5 py-2 font-sans font-medium uppercase tracking-wider text-[10px]">Stellplätze</th>
                <th className="text-right px-5 py-2 font-sans font-medium uppercase tracking-wider text-[10px]">Jahresmiete</th>
              </tr>
            </thead>
            <tbody>
              {(kpis?.pro_gebaeude || []).map((g) => (
                <tr key={g.gebaeude} className="border-b border-navy/5 hover:bg-navy/3">
                  <td className="px-5 py-2.5 text-navy font-medium">{g.gebaeude}</td>
                  <td className="px-5 py-2.5 text-right text-navy/75 font-sans">{fmtInt(g.einheiten)}</td>
                  <td className="px-5 py-2.5 text-right text-navy/75 font-sans">{fmtQm(g.mietflaeche_m2)}</td>
                  <td className="px-5 py-2.5 text-right text-navy/75 font-sans">{fmtInt(g.stellplaetze)}</td>
                  <td className="px-5 py-2.5 text-right text-navy font-medium font-sans">{fmtEur(g.jahresnettomiete_eur)}</td>
                </tr>
              ))}
              {(!kpis?.pro_gebaeude || !kpis.pro_gebaeude.length) && (
                <tr><td colSpan={5} className="px-5 py-4 text-sm text-navy/55">Keine Daten</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Rent Roll Tabelle */}
      <section className="border border-navy/12 bg-white" data-testid="rentroll-table">
        <div className="px-5 pt-5 pb-3 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="pb-eyebrow">Rent Roll</div>
            <div className="font-display text-lg text-navy mt-1">
              {filteredRows.length} {filteredRows.length === 1 ? "Mieteinheit" : "Mieteinheiten"}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={14} strokeWidth={1.6} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-navy/40" />
              <input
                data-testid="rentroll-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Mieter, Nutzung, Gebäude…"
                className="pl-8 pr-7 py-1.5 text-xs border border-navy/15 focus:outline-none focus:border-navy text-navy font-sans w-56"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy p-1"
                  aria-label="Suche zurücksetzen"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <select
              data-testid="rentroll-filter-gebaeude"
              value={gebaeudeFilter}
              onChange={(e) => setGebaeudeFilter(e.target.value)}
              className="px-2.5 py-1.5 text-xs border border-navy/15 bg-white text-navy focus:outline-none focus:border-navy font-sans"
            >
              <option value="">Alle Gebäude</option>
              {gebaeudeOptions.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-y border-navy/10 bg-navy/3 text-navy/65">
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => handleSort(c.key)}
                    className={`px-3 py-2 font-sans font-medium uppercase tracking-wider text-[10px] cursor-pointer select-none hover:text-navy whitespace-nowrap ${c.align === "right" ? "text-right" : "text-left"}`}
                    data-testid={`rentroll-sort-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sortBy === c.key ? (
                        sortDir === "asc"
                          ? <ArrowUp size={10} />
                          : <ArrowDown size={10} />
                      ) : (
                        <ArrowUpDown size={10} className="opacity-30" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={COLUMNS.length} className="px-5 py-4 text-navy/55">Lade Daten…</td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={COLUMNS.length} className="px-5 py-4 text-navy/55">Keine Mieteinheiten entsprechen den Filtern.</td></tr>
              )}
              {filteredRows.map((r) => (
                <tr
                  key={r.id}
                  data-testid="rentroll-row"
                  className="border-b border-navy/5 hover:bg-navy/3"
                >
                  <td className="px-3 py-2 text-navy font-medium whitespace-nowrap">{r.gebaeude || "—"}</td>
                  <td className="px-3 py-2 text-navy">
                    <div className="truncate max-w-[180px]" title={r.mieter || ""}>{r.mieter || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-navy/75">
                    <div className="truncate max-w-[180px]" title={r.nutzung || ""}>{r.nutzung || "—"}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_CHIP[r.status] || STATUS_CHIP.unbekannt}`}>
                      {STATUS_LABEL[r.status] || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-navy/85 font-sans">
                    {r.mietflaeche_m2 != null ? fmtDec(r.mietflaeche_m2) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-navy/85 font-sans">
                    {r.stellplaetze != null ? fmtInt(r.stellplaetze) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-navy font-medium font-sans">
                    {r.jahresnettomiete_eur != null ? fmtEur(r.jahresnettomiete_eur) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-navy/85 font-sans">
                    {r.eur_pro_m2 != null ? fmtDec(r.eur_pro_m2) : "—"}
                  </td>
                  <td className="px-3 py-2 text-navy/75 whitespace-nowrap">
                    {r.mietende ? fmtDate(r.mietende) : (r.mietende_text || "—")}
                  </td>
                  <td className="px-3 py-2 text-navy/65">
                    <div className="truncate max-w-[120px]" title={r.indexierungsart || ""}>
                      {r.indexierungsart || "—"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Datenraum-Hinweis */}
      <section
        data-testid="dataroom-hint"
        className="border border-navy/12 bg-ledger px-5 py-4 flex items-start gap-3"
      >
        <Activity size={18} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
        <div className="text-sm text-navy/80 font-sans">
          Die Quelldatei „Übersicht Vermietung 1.2.xlsx" liegt im
          {" "}<span className="font-medium text-navy">Datenraum &gt; 06_Mietverträge</span>{" "}
          und ist für Investoren <span className="font-medium">nach NDA-Unterzeichnung</span> sichtbar.
        </div>
      </section>
    </div>
  );
}
